import React from 'react';
import PropTypes from "prop-types";
import { TextField, CircularProgress, InputAdornment, Typography } from '@mui/material';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';

import Api from "../../services/api";
import Api2Talk from "../../services/api2talk";
import { PapiroConsole } from '../../utils/papiroConsole';

const BRAZIL_PHONE_PREFIX = "55";

const StyledPopper = styled(Popper)({
	[`& .${autocompleteClasses.listbox}`]: {
		boxSizing: 'border-box',
		'& ul': {
			padding: 0,
			margin: 0,
		},
	},
});

const getOptionDisplayLabel = (option) => {
	if (!option) {
		return '';
	}

	if (typeof option === 'string') {
		return option;
	}

	if (option.parentname) {
		return option.parentname;
	}

	if (option.name && option.email && option.phone) {
		return `${option.name} (${option.email} | ${option.phone})`;
	}

	if (option.email) {
		return `${option.name} (${option.email})`;
	}

	if (option.title && option.id) {
		return `${option.id} : ${option.title}`;
	}

	return option.name || option.phone || '';
};

const getPhoneInputValue = (phone) => {
	const rawPhone = phone ? `${phone}` : '';
	const digits = rawPhone.replace(/\D/g, "");

	if (digits.startsWith(BRAZIL_PHONE_PREFIX)) {
		return digits.slice(BRAZIL_PHONE_PREFIX.length);
	}

	return digits;
};

const getOptionInputValue = (option) => getPhoneInputValue(option && option.phone);

export default function SimpleAutoCompleteSearchPhoneBr(props) {
	const { label, options, stateName, changeSelect, selected, required, disabled, urlapi, sendfrom2talk, numberdigits, phoneValue, handleInputChange: handleInputChangeProp } = props;

	const totaldigits = numberdigits != null ? numberdigits : 5;
	const defaultvalue = { id: 0, name: '' };

	const [opt, setOption] = React.useState(selected ? selected : defaultvalue);
	const [items, setItems] = React.useState(options);
	const [loading, setLoading] = React.useState(false);
	const [inputValue, setInputValue] = React.useState('');
	const [typingTimeout, setTypingTimeout] = React.useState(0);
	const previousController = React.useRef();

	React.useEffect(() => {
		setOption(selected ? selected : defaultvalue);
		setInputValue(selected ? getOptionInputValue(selected) : getPhoneInputValue(phoneValue));
	}, [selected, phoneValue]);

	React.useEffect(() => {
		setItems(options);
	}, [options]);

	const getData = async (searchTerm) => {
		if (previousController.current) {
			previousController.current.abort();
		}

		if ((searchTerm + '').length < totaldigits || loading) {
			return false;
		}

		setLoading(true);
		const controller = new AbortController();
		const signal = controller.signal;
		previousController.current = controller;

		const api = sendfrom2talk == true ? Api2Talk : Api;
		await api.get(urlapi + "/" + searchTerm, {
			signal,
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json"
			}
		})
			.then(function (response) {
				setItems(response.data.data);
				setLoading(false);
			}).catch(err => {
				setLoading(false);
				setItems([]);
			});
	};

	const handleChange = (event, value) => {
		if (value == null) {
			value = defaultvalue;
			setInputValue('');
			if (handleInputChangeProp) {
				handleInputChangeProp('');
			}
		}
		else {
			setInputValue(getOptionInputValue(value));
		}

		setOption(value);
		changeSelect(stateName, value);
	};

	const handleInputTextChange = (event, value, reason) => {
		if (reason !== 'input' && reason !== 'clear') {
			return;
		}

		const digits = (value || "").replace(/\D/g, "");
		const fullPhone = digits ? `${BRAZIL_PHONE_PREFIX}${digits}` : "";

		setInputValue(digits);

		if (handleInputChangeProp) {
			handleInputChangeProp(fullPhone);
		}

		if (!digits) {
			setItems([]);
			return;
		}

		if (reason === 'input' && digits.length >= totaldigits) {
			if (typingTimeout) {
				clearTimeout(typingTimeout);
			}

			setTypingTimeout(setTimeout(function () {
				getData(fullPhone);
			}, 2000));
		}
	};

	return (
		<Autocomplete
			id="combo-box-search-auto-complete-phone-br"
			autoHighlight
			style={props.style != null ? props.style : { fullWidth: true }}
			PopperComponent={StyledPopper}
			loading={loading}
			onChange={handleChange}
			disabled={disabled}
			value={opt}
			inputValue={inputValue}
			options={items}
			onInputChange={handleInputTextChange}
			getOptionLabel={option => option.phone ? option.phone : ''}
			renderInput={params => (
				<Tooltip title={<h5>{params.inputProps.value}</h5>} placement="top">
					<TextField
						{...params}
						label={label}
						variant="outlined"
						required={required != null ? required == false ? false : true : false}
						disabled={props.disabled}
						fullWidth
						InputProps={{
							...params.InputProps,
							startAdornment: (
								<>
									<InputAdornment position="start">+{BRAZIL_PHONE_PREFIX}</InputAdornment>
									{params.InputProps.startAdornment}
								</>
							),
							endAdornment: (
								<>
									{loading ? <CircularProgress color="inherit" size={20} /> : null}
									{params.InputProps.endAdornment}
								</>
							)
						}}
					/>
				</Tooltip>
			)}
			renderOption={(props, option) => (
				<Typography component="li" {...props} noWrap>
					{getOptionDisplayLabel(option)}
				</Typography>
			)}
			onKeyDown={(event) => {
				if (event.key === 'Enter') {
					event.defaultMuiPrevented = true;
				}
			}}
			freeSolo
		/>
	);
}

SimpleAutoCompleteSearchPhoneBr.propTypes = {
	label: PropTypes.string,
	options: PropTypes.array,
	stateName: PropTypes.string,
	changeSelect: PropTypes.func,
	handleInputChange: PropTypes.func,
	phoneValue: PropTypes.string,
	selected: PropTypes.object,
	urlapi: PropTypes.string
};
